import { Show, type Component } from 'solid-js';

import { respawn } from '../net/protocol';
import type { GameStore } from '../state/store';

/** Thousands separators, so a five-figure Fame total stays readable. */
export function formatTally(n: number): string {
  return n.toLocaleString('en-US');
}

/**
 * The perma-death screen, per `doc/grind/design/HUD-UX.md`.
 *
 * Death is a game state, not a connection state: this sits over a canvas that is
 * still rendering and a socket that is still open, and CMD_RESPAWN puts the
 * player back without either being torn down.
 *
 * The Fame ledger renders only when the server sends a summary. Milestone 1 has
 * no levels, bosses or Fame, so what appears is the death and the buttons — not
 * a row of invented zeroes.
 *
 * **One way out, deliberately.** There used to be two — rebirth at the zone entry,
 * and a retreat to town — because rebirth on its own was a trap: mobs do not respawn
 * but a woken pack does not go back to sleep either, so reviving landed the player
 * beside the group that had just killed them. Waking in town *is* the retreat now, so
 * the second button would send the same command by another name, and a screen with two
 * buttons that do one thing is worse than a screen with one.
 *
 * That is why the button says where it goes. "Rebirth" alone would leave the player
 * to discover that the run is over by arriving home, and losing the zone is the whole
 * cost of dying — the only thing left that ends a dive.
 */
export const RebirthModal: Component<{ store: GameStore }> = (props) => (
  <Show when={props.store.dead()}>
    <div
      class="rebirth hud-interactive"
      data-testid="rebirth-modal"
      role="dialog"
      aria-modal="true"
      aria-label="You died"
    >
      <h1 class="rebirth__title">YOU DIED</h1>
      <p class="rebirth__epitaph">The world does not pause for you.</p>
      <p class="rebirth__epitaph" data-testid="rebirth-consequence">
        The zone is lost. You wake in the Sanctuary, healed and restocked.
      </p>

      <Show when={props.store.rebirth()}>
        {(summary) => (
          <dl class="rebirth__ledger" data-testid="rebirth-ledger">
            <div class="rebirth__row">
              <dt>Level Reached</dt>
              <dd>{summary().level}</dd>
            </div>
            <div class="rebirth__row">
              <dt>Bosses Felled</dt>
              <dd>{formatTally(summary().bossKills)}</dd>
            </div>
            <div class="rebirth__row">
              <dt>Rivals Slain</dt>
              <dd>{formatTally(summary().pvpKills)}</dd>
            </div>
            <div class="rebirth__row">
              <dt>Fame Earned</dt>
              <dd>{formatTally(summary().fame)}</dd>
            </div>
            <div class="rebirth__row rebirth__row--total">
              <dt>Rebirth Points</dt>
              <dd>{formatTally(summary().rebirthPoints)}</dd>
            </div>
          </dl>
        )}
      </Show>

      <div class="rebirth__actions">
        <button
          type="button"
          class="sigil-button rebirth__button"
          data-testid="rebirth-button"
          onClick={() => props.store.send(respawn())}
        >
          <span>Wake in the Sanctuary</span>
        </button>
      </div>
    </div>
  </Show>
);
